import { useState } from "react";
import type { Exercise, AttemptResult } from "../../types";
import { api } from "../../api/client";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

export default function MultipleChoiceExercise({ exercise, onDone, onSkip }: Props) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const options = (exercise.options as string[] | null) ?? [];

  const pick = async (option: string) => {
    if (result || submitting) return;
    setChosen(option);
    setSubmitting(true);
    try {
      setResult(await api.attemptExercise(exercise.id, option));
    } catch (err) {
      console.error("Attempt failed:", err);
      setChosen(null);
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setResult(null);
    setChosen(null);
  };

  const styleFor = (option: string): string => {
    if (!result) return "border-border hover:border-text-tertiary hover:bg-surface2";
    if (result.is_correct && option === result.correct_answer) return "border-success/60 bg-success/10 text-success";
    if (!result.is_correct && option === chosen) return "border-error/60 bg-error/10 text-error";
    return "border-border opacity-60";
  };

  return (
    <div>
      <p className="text-[1.05rem] text-ink leading-relaxed font-display mb-5">{exercise.text}</p>
      <div className="flex flex-col gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => pick(opt)}
            disabled={!!result || submitting}
            className={`text-left px-4 py-3 rounded-[10px] border text-sm transition-colors ${styleFor(opt)}`}
          >
            {opt}
          </button>
        ))}
      </div>

      {!result && onSkip && (
        <div className="flex justify-end mt-3">
          <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>
        </div>
      )}

      {result && (
        <FeedbackPanel correct={result.is_correct} onNext={() => onDone(result.is_correct)} onRetry={retry}>
          {result.explanation && (result.is_correct || !retry) && <div className="italic">{result.explanation}</div>}
        </FeedbackPanel>
      )}
    </div>
  );
}
